import { HttpClient } from "@angular/common/http";
import { Injectable, Inject, Optional } from "@angular/core";

import { Observable, from, map, switchMap, throwError } from "rxjs";

import { ENV } from "@dffedb/util";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { UmbracoHttpClient } from "@e-forsyning/common/http";

export interface ServiceProFeedbackMailPayload {
    sideId: string;
    fromEmail: string;
    subject: string;
    body: string;
    type: string | null;
    user?: string | null;
    vaerk?: string | null;
    deviceType?: string | null;
    version?: string | null;
    userAgent?: string | null;
    timestamp?: string | null;
    imageBase64?: string | null;
    imageFileName?: string | null;
    imageContentType?: string | null;
}

export interface ServiceProFeedbackImage {
    base64: string;
    fileName: string;
    contentType: string;
}

@Injectable({ providedIn: "root" })
export class ServiceProFeedbackMailService {
    constructor(
        private readonly umbracoHttp: UmbracoHttpClient,
        private readonly http: HttpClient,
        private readonly credentialsRepository: AppserverInstallationCredentialsRepository,
        @Optional() @Inject(ENV) private readonly env: { umbracoServerUrl?: string; feedbackUmbracoServerUrl?: string } | null
    ) {}

    public send(payload: Omit<ServiceProFeedbackMailPayload, "sideId"> & { sideId?: string }): Observable<void> {
        const credentials = this.credentialsRepository.hent();
        const sideId = payload.sideId || (credentials?.forsyningNodeId ? String(credentials.forsyningNodeId) : "");
        if (!sideId) {
            return throwError(() => new Error("Mangler værk-kontekst. Log ind igen."));
        }

        const body: ServiceProFeedbackMailPayload = {
            sideId,
            fromEmail: (payload.fromEmail || "").trim(),
            subject: (payload.subject || "").trim(),
            body: (payload.body || "").trim(),
            type: payload.type || null,
            user: payload.user || null,
            vaerk: payload.vaerk || null,
            deviceType: payload.deviceType || null,
            version: payload.version || null,
            userAgent: payload.userAgent || null,
            timestamp: payload.timestamp || null,
            imageBase64: payload.imageBase64 || null,
            imageFileName: payload.imageFileName || null,
            imageContentType: payload.imageContentType || null
        };

        const path = "/umbraco/dff/dffapi/SendServiceProFeedbackMail";
        const feedbackBase = (this.env?.feedbackUmbracoServerUrl || "").trim().replace(/\/$/, "");

        if (feedbackBase) {
            return this.http.post(feedbackBase + path, body, { responseType: "text" }).pipe(map(() => undefined));
        }

        return this.umbracoHttp.postWithStringResult(path, body).pipe(map(() => undefined));
    }

    public blobToImage(blob: Blob, fileName = "screenshot.png"): Observable<ServiceProFeedbackImage> {
        return from(
            new Promise<ServiceProFeedbackImage>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = typeof reader.result === "string" ? reader.result : "";
                    const commaIndex = result.indexOf(",");
                    const base64 = commaIndex >= 0 ? result.slice(commaIndex + 1) : result;
                    if (!base64) {
                        reject(new Error("Kunne ikke læse skærmbillede."));
                        return;
                    }
                    resolve({
                        base64,
                        fileName,
                        contentType: blob.type || "image/png"
                    });
                };
                reader.onerror = () => reject(new Error("Kunne ikke læse skærmbillede."));
                reader.readAsDataURL(blob);
            })
        );
    }

    public sendWithOptionalScreenshot(
        fromEmail: string,
        subject: string,
        body: string,
        screenshot: Blob | null,
        sideId?: string,
        metadata?: Pick<ServiceProFeedbackMailPayload, "type" | "user" | "vaerk" | "deviceType" | "version" | "userAgent" | "timestamp">
    ): Observable<void> {
        if (!screenshot) {
            return this.send({ fromEmail, subject, body, sideId, ...metadata });
        }

        return this.blobToImage(screenshot).pipe(
            switchMap((image) =>
                this.send({
                    fromEmail,
                    subject,
                    body,
                    sideId,
                    ...metadata,
                    imageBase64: image.base64,
                    imageFileName: image.fileName,
                    imageContentType: image.contentType
                })
            )
        );
    }
}
